import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Paper,
  Typography,
  Tooltip,
  IconButton,
  Grid,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable/';
import NewCustomerDialog from 'components/popups/NewCustomerDialog';
import EditCustomerDialog from 'components/popups/EditCustomerDialog';
import CustomerService from 'services/CustomerService';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import { ROUTES, USER_PERMISSIONS } from 'const';
import { useHistory } from 'react-router-dom';
import { AlertMessagesContext } from 'react-alert-messages';
import ShopsHelper from 'helpers/ShopsHelper';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CsvHelper from 'helpers/CsvHelper';
import { saveAs } from 'file-saver';
import ArrayHelper from 'helpers/ArrayHelper';
import SyncIcon from '@mui/icons-material/Sync';
import EditIcon from '@mui/icons-material/Edit';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import PrintIcon from '@mui/icons-material/Print';
import { SelectFilterStyle } from 'const';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import CustomTablePagination from 'components/utils/Pagination/CustomTablePagination';
import { useTranslation } from 'react-i18next';
import CsvExportPopup from 'components/popups/CsvExportPopup';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';

const PAGE_KEY = 'CUSTOMERS';

const FILTER_CUSTOMER_BALANCE = {
  excess: 'Excess',
  due: 'Due',
  nil: 'Zero',
};

function Customers() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const history = useHistory();

  const [customers, setCustomers] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [isShowCreateDialog, setIsShowCreateDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [count, setCount] = useState(0);
  const [isEnableSearchRefreshButton, setIsEnableSearchRefreshButton] = useState(false);
  const [filterGroup, setFilterGroup] = useState([]);
  const [filteredCustomerGroups, setFilteredCustomerGroups] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [filterBalance, setFilterBalance] = useState([]);
  const [filteredCustomerBalances, setFilteredCustomerBalances] = useState([]);
  const [isShowExportCsvPopup, setIsShowExportCsvPopup] = useState(false);

  const userHasCustomersWritePermission = user?.shop_permissions?.customers_permission === USER_PERMISSIONS.WRITE;
  const featureValidity = shop?.customer_support_validity;

  const openEditCustomerDialog = (customer) => {
    setEditData(customer);
  };

  const closeEditCustomerDialog = () => {
    setEditData(null);
  };

  const openCreateNewCustomerDialog = () => {
    setIsShowCreateDialog(true);
  };

  const closeCreateNewCustomerDialog = () => {
    setIsShowCreateDialog(false);
  };

  const getCustomers = useCallback(
    async ({
      _offset = 0,
      _limit = 50,
      search,
      groups = filteredCustomerGroups,
      balance_types = filteredCustomerBalances,
    } = {}) => {
      setLoadingIndicator(true);
      try {
        const response = await CustomerService.getCustomersWithCount({
          offset: _offset,
          limit: _limit,
          search: search || undefined,
          group_ids: groups.length ? groups.join(',') : undefined,
          balance_types: balance_types.length ? balance_types.join(',') : undefined,
        });

        const customers = response?.data || [];
        const totalCount = response?.count || customers.length;

        setCustomers(customers);
        setCount(totalCount);

        setIsEnableSearchRefreshButton(false);
      } catch (error) {
        postAlertMessage({ text: error.message, type: 'failed' });
      }
      setLoadingIndicator(false);
    },
    [filteredCustomerGroups, filteredCustomerBalances, postAlertMessage]
  );

  const redirectToCustomerrDetails = async (data) => {
    history.push({
      pathname: `${ROUTES.CUSTOMERS}/${data.id}`,
    });
  };

  const getBalance = (data) => {
    const balance = ShopsHelper.getAmountFormatted(shop, data?.balance);
    let balanceStyle;
    if (balance >= 0) balanceStyle = { color: 'green' };
    else if (balance > data?.credit_limit) balanceStyle = { color: 'orange' };
    else balanceStyle = { color: 'red' };
    return (
      <Typography variant="body2" style={balanceStyle} gutterBottom>
        {formatBalanceWithCr(balance)}
      </Typography>
    );
  };

  const CSV_COLUMNS = {
    id: 'id',
    code: 'code',
    name: 'name',
    email: 'email',
    mobile: 'mobile',
    location: 'place',
    group: {
      title: 'group',
      getValue: (item) => {
        return item?.group?.name || '';
      },
    },
    credit_limit: 'credit_limit',
    trn_number: 'trn',
    balance: {
      title: 'balance',
      getValue: (item) => {
        return ShopsHelper.getAmountFormatted(shop, item?.balance || 0);
      },
    },
  };

  const headerData = [
    {
      label: tCommon('ID'),
      id: 'id',
      type: 'text',
    },
    {
      label: tCommon('Code'),
      id: 'code',
      type: 'text',
    },
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Mobile'),
      id: 'mobile',
      type: 'text',
    },
    {
      label: tCommon('Place'),
      id: 'location',
      type: 'text',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
    },
    {
      label: tCommon('Group'),
      id: 'group',
      type: 'callback',
      viewRender: (data) => data?.group?.name ?? 'None',
      cssClass: filteredCustomerGroups.length ? 'no-print' : '',
      cssClassHeader: filteredCustomerGroups.length ? 'no-print' : '',
    },
    {
      label: tCommon('Credit Limit'),
      id: 'credit_limit',
      type: 'callback',
      viewRender: (customer) => formatBalanceWithCr(customer.credit_limit),
    },
    {
      label: tCommon('TRN'),
      id: 'trn_number',
      type: 'text',
    },
    {
      label: tCommon('Balance'),
      id: 'balance',
      type: 'callback',
      viewRender: (data) => getBalance(data),
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      cssClass: 'no-print',
      cssClassHeader: 'no-print',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tCommon('View Details')} onClick={() => redirectToCustomerrDetails(item)}>
              <RemoveRedEyeIcon className={styles.iconButton} />
            </Tooltip>
            {userHasCustomersWritePermission && (
              <>
                <Tooltip title={tCommon('Edit Customer')} onClick={() => openEditCustomerDialog(item)}>
                  <EditIcon className={styles.iconButton} />
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    },
  ];

  const toCsvBtnPressed = async () => {
    setIsShowExportCsvPopup(true);
  };

  const handleExportCsv = async (selectedColumns) => {
    const csv = CsvHelper.getString(customers, selectedColumns);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `CUSTOMERS-${dateTime}.csv`);

    postAlertMessage({
      text: 'Exported to excel successfully',
      type: 'success',
    });
  };

  const handleImportCsv = async (csvFile) => {
    setLoadingIndicator(true);

    try {
      const csvData = await CsvHelper.parseCsvFile(csvFile);
      const safeDecode = (str) => {
        try {
          return str ? decodeURIComponent(str) : str;
        } catch (e) {
          return str;
        }
      };
      const customers = csvData.map((row, index) => {
        if (!row.name) {
          throw new Error(tOperations('Name must be provided'));
        } else if (!row.mobile) {
          throw new Error(tOperations('Mobile must be provided'));
        }
        const group = customerGroups.find((g) => g.name === row.group) || {};
        const decodedName = safeDecode(row.name);
        return {
          name: decodedName,
          email: row.email,
          mobile: row.mobile,
          location: safeDecode(row.place || row.location),
          balance: Number(row.balance),
          credit_limit: Number(row.creditlimit),
          trn_number: row.trn || row.trnnumber,
          code: row.code,
          group: {
            id: group.id || null,
            name: row.group || null,
          },
        };
      });

      const alertId = 'customers-batch-update';
      const customersChunked = ArrayHelper.chunk(customers, 40);
      postAlertMessage({
        key: alertId,
        text: `Updating customers as ${customersChunked.length} segments`,
        timeout: -1,
      });
      for (const [index, customersChunk] of customersChunked.entries()) {
        await CustomerService.updateCustomers(customersChunk);
        postAlertMessage({
          key: alertId,
          text: `${index + 1}th chunk uploaded successfully`,
          timeout: -1,
        });
      }

      getCustomers();

      postAlertMessage({
        key: alertId,
        text: 'Customers imported successfully',
        type: 'success',
      });
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed', timeout: 5000 });
    }
    setLoadingIndicator(false);
  };

  const getCustomerGroups = async () => {
    const resp = await CustomerService.getCustomerGroups();
    setCustomerGroups(resp);
  };

  useEffect(() => {
    if (!shop?.id || featureIsExpired(featureValidity)) return;
    getCustomers();
  }, [shop?.id, featureValidity, filteredCustomerGroups, getCustomers]);

  useEffect(() => {
    if (!shop?.id || featureIsExpired(featureValidity)) return;
    getCustomerGroups();
  }, [featureValidity, shop?.id]);

  const totalBalance = ShopsHelper.getAmountFormatted(
    shop,
    customers?.reduce((sum, customer) => sum + (customer.balance ?? 0), 0) ?? 0
  );
  return (
    <div className={styles.contentWrapper}>
      <div className={`${styles.titleSec} no-print`}>
        <h2 className={styles.title}>
          {tCommon('Customer')}
          <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
        {shop && !featureIsExpired(featureValidity) && (
          <div className="buttons" style={{ display: 'flex', justifyContent: 'flex-end', gap: 5 }}>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Import As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#676dd8' }}
              >
                <input
                  accept="text/csv"
                  type="file"
                  hidden
                  id="import"
                  onChange={(event) => {
                    const fileField = event.target;
                    const csvFile = fileField.files[0];
                    fileField.value = null;
                    handleImportCsv(csvFile);
                  }}
                />
                <ImportExportIcon className={styles.actionBtnIcon} />
                <label for="import">{tCommon('CSV')}</label>
              </Button>
            </div>
            <div style={{ justifyContent: 'flex-end' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Print Report')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={window.print}
              >
                <PrintIcon className={styles.actionBtnIcon} />
                {tCommon('Print')}
              </Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ paddingBottom: '4px' }}>
                <label className={styles.label}>{tCommon('Export As')}</label>
              </div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={toCsvBtnPressed}
              >
                <ImportExportIcon className={styles.actionBtnIcon} />
                {tCommon('CSV')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {shop && featureIsExpired(featureValidity) ? (
        <RestrictionInfo
          title={tOperations('Customer Support Feature Not Available')}
          content={tOperations('To get customer support options, contact support team.')}
        />
      ) : (
        <>
          <div className={styles.changeable}>
            <Grid container>
              <Grid item xs={12} sm={2} className={styles.headTitle}>
                <h2 className={styles.subTitle}>{tCommon('Customers')}</h2>
              </Grid>

              <Grid item xs={12} sm={10} className={styles.actionButtons}>
                <Grid container spacing={1} className={styles.filterDiv}>
                  <Grid container item xs={6} sm={3} className={`${styles.filerInputSec} no-print`}>
                    {userHasCustomersWritePermission && (
                      <>
                        <Grid item xs={12} style={{ paddingBottom: '4px' }}>
                          <label className={styles.label}>{tCommon('New Customer')}</label>
                        </Grid>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          fullWidth
                          style={{ backgroundColor: '#ff851b', height: 40 }}
                          className={styles.actionBtn}
                          onClick={openCreateNewCustomerDialog}
                        >
                          <NoteAddIcon className={styles.actionBtnIcon} />
                          {tCommon('New Customer')}
                        </Button>
                      </>
                    )}
                  </Grid>
                  <Grid container item xs={6} sm={3}>
                    <Grid item xs={12} style={{ paddingBottom: '4px' }}>
                      <label className={styles.label}>{tCommon('Search Customer')}</label>
                    </Grid>
                    <Grid item xs={12} className={styles.searchSec}>
                      <input
                        type="text"
                        value={filterText}
                        onChange={(e) => {
                          setFilterText(e.target.value);
                          setIsEnableSearchRefreshButton(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const value = e.target.value.trim();
                            getCustomers({ _offset: 0, search: value });
                          }
                        }}
                        className={styles.searchInput}
                        placeholder={tCommon('Search Customers')}
                      />
                      <Tooltip title="Refresh">
                        <IconButton
                          className={styles.iconBtn}
                          onClick={() => {
                            getCustomers({ _offset: 0 });
                          }}
                          disabled={!isEnableSearchRefreshButton}
                        >
                          <SyncIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                  {customerGroups.length > 0 && (
                    <Grid container item xs={6} sm={3}>
                      <Grid item xs={12} style={{ paddingBottom: '4px' }}>
                        <label className={styles.label}>{tCommon('Customer Groups')}</label>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Select
                            labelId="group-multiple-checkbox-label"
                            id="group-multiple-checkbox"
                            multiple
                            fullWidth
                            displayEmpty
                            value={filterGroup}
                            onChange={(e) => {
                              setFilterGroup(e.target.value);
                              setFilteredCustomerGroups(e.target.value);
                            }}
                            input={<OutlinedInput className={styles.selectInput} />}
                            style={{ height: 40 }}
                            renderValue={(selected) =>
                              selected.length === 0
                                ? 'All'
                                : selected.map((id) => customerGroups.find((grp) => grp.id === id)?.name).join(', ')
                            }
                            MenuProps={SelectFilterStyle}
                          >
                            {customerGroups.map((group) => (
                              <MenuItem key={group.id} value={group.id}>
                                <Checkbox checked={filterGroup.includes(group.id)} color="primary" />
                                <ListItemText primary={group.name} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                  {customers ? (
                    <Grid container item xs={6} sm={3} className={styles.filterDiv}>
                      <Grid item xs={12} style={{ paddingBottom: '4px' }}>
                        <label className={styles.label}>{tCommon('Customer Balance')}</label>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <Select
                            labelId="balance-multiple-checkbox-label"
                            id="balance-multiple-checkbox"
                            multiple
                            fullWidth
                            displayEmpty
                            value={filterBalance}
                            onChange={(e) => {
                              const selectedBalances = e.target.value;
                              setFilterBalance(selectedBalances);
                              setFilteredCustomerBalances(selectedBalances);
                            }}
                            input={<OutlinedInput className={styles.selectInput} />}
                            style={{ height: 40 }}
                            renderValue={(selectedBalances) => {
                              if (selectedBalances.length === 0) return <span>{tCommon('All')}</span>;
                              const selectedNames = selectedBalances.map((balance) => FILTER_CUSTOMER_BALANCE[balance]);
                              return selectedNames.join(', ');
                            }}
                            MenuProps={SelectFilterStyle}
                          >
                            {Object.entries(FILTER_CUSTOMER_BALANCE).map(([key, label]) => (
                              <MenuItem key={key} value={key}>
                                <Checkbox checked={filterBalance.includes(key)} color="primary" />
                                <ListItemText primary={label} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  ) : null}
                </Grid>
              </Grid>
            </Grid>
          </div>
          {(customers && customers.length) || loadingIndicator ? (
            <Paper className={styles.tableWrapper}>
              <DataTable
                columns={headerData}
                rows={customers}
                isLoading={loadingIndicator}
                deactiveRow
                summary={
                  loadingIndicator
                    ? null
                    : {
                        trn_number: tCommon('Total*'),
                        balance: totalBalance,
                      }
                }
              />
              <CustomTablePagination
                storageKey={PAGE_KEY}
                count={count}
                onFetch={getCustomers}
                defaults={{ page: 0, limit: 50 }}
                rowsPerPageOptions={[50, 100, 200]}
              />
            </Paper>
          ) : (
            !loadingIndicator && (
              <Info
                title={tOperations('No Customers Found')}
                content={tOperations('You have no customers to list. Create a new customer to get started.')}
              />
            )
          )}
          {editData && (
            <EditCustomerDialog
              closeDialog={closeEditCustomerDialog}
              editData={editData}
              reloadCustomers={getCustomers}
              customerGroups={customerGroups}
            />
          )}
          {isShowCreateDialog && (
            <NewCustomerDialog
              customerGroups={customerGroups}
              closeCreateNewCustomerDialog={closeCreateNewCustomerDialog}
              reloadCustomers={getCustomers}
            />
          )}
          {isShowExportCsvPopup && (
            <CsvExportPopup
              title={tCommon('Export csv')}
              handleClose={() => setIsShowExportCsvPopup(false)}
              onExport={handleExportCsv}
              columnMapping={CSV_COLUMNS}
            />
          )}
        </>
      )}
    </div>
  );
}

export default withConsoleBase(Customers);
